import React, { useEffect, useState, useMemo } from 'react';
import { History, RefreshCw, Download } from 'lucide-react';
import { AuditService, AUDIT_LABELS } from '../services/auditService';
import { formatDateTime, convertFirestoreTimestamp } from '../utils/helpers';
import { toCsv, downloadText } from '../utils/finance';

// Journal d'activité du cabinet (lecture seule, accès complet uniquement)
const AuditLog = ({ cabinetId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');

  const load = async () => {
    setLoading(true);
    const r = await AuditService.list(cabinetId);
    setItems(r.items);
    setLoading(false);
  };

  useEffect(() => { if (cabinetId) load(); }, [cabinetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const users = useMemo(() => Array.from(new Set(items.map((i) => i.userName).filter(Boolean))).sort(), [items]);
  const actions = useMemo(() => Array.from(new Set(items.map((i) => i.action))).sort(), [items]);
  const filtered = items.filter((i) => (!filterUser || i.userName === filterUser) && (!filterAction || i.action === filterAction));

  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((i) => {
      const d = convertFirestoreTimestamp(i.createdAt);
      const key = d ? new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Date inconnue';
      (g[key] = g[key] || []).push(i);
    });
    return g;
  }, [filtered]);

  const exportCsv = () => downloadText(`journal_${new Date().toISOString().slice(0, 10)}.csv`, toCsv(filtered, [
    { label: 'Date', value: (i) => formatDateTime(convertFirestoreTimestamp(i.createdAt)) }, { label: 'Utilisateur', value: 'userName' },
    { label: 'Rôle', value: 'role' }, { label: 'Action', value: (i) => AUDIT_LABELS[i.action] || i.action }, { label: 'Détail', value: 'label' }
  ]));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)} className="field sm:w-52" aria-label="Utilisateur">
          <option value="">Tous les utilisateurs</option>{users.map((u) => <option key={u}>{u}</option>)}
        </select>
        <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} className="field sm:w-60" aria-label="Action">
          <option value="">Toutes les actions</option>{actions.map((a) => <option key={a} value={a}>{AUDIT_LABELS[a] || a}</option>)}
        </select>
        <div className="flex gap-2 sm:ml-auto">
          <button onClick={exportCsv} className="btn-secondary"><Download className="h-4 w-4" /> CSV</button>
          <button onClick={load} className="btn-secondary" aria-label="Actualiser"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-sm text-slate-500">Chargement du journal…</div>
      ) : filtered.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          <History className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Aucune activité enregistrée</p>
        </div>
      ) : (
        Object.entries(groups).map(([day, list]) => (
          <div key={day} className="card overflow-hidden">
            <div className="px-4 sm:px-5 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500 capitalize">{day}</div>
            <ul className="divide-y divide-slate-100">
              {list.map((i) => (
                <li key={i.id} className="px-4 sm:px-5 py-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-800 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {(i.userName || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-800"><span className="font-medium">{i.userName || 'Utilisateur'}</span> <span className="text-slate-500">· {AUDIT_LABELS[i.action] || i.action}</span></p>
                    {i.label && <p className="text-sm text-slate-600 truncate">{i.label}</p>}
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap tabular-nums">
                    {(() => { const d = convertFirestoreTimestamp(i.createdAt); return d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''; })()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
};

export default AuditLog;
